import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { loginApi, registerApi, getMeApi, logoutApi, sessionHeartbeat } from './api';

const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

function formatApiError(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const heartbeatRef = useRef(null);

  const startHeartbeat = useCallback((sessionId) => {
    if (heartbeatRef.current) clearInterval(heartbeatRef.current);
    heartbeatRef.current = setInterval(() => {
      sessionHeartbeat({ session_id: sessionId }).catch(() => {});
    }, 60000);
  }, []);

  const stopHeartbeat = useCallback(() => {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
  }, []);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('sustek_token');
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await getMeApi();
      setUser(data);
    } catch {
      localStorage.removeItem('sustek_token');
      localStorage.removeItem('sustek_session_id');
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  // Start heartbeat when user is logged in
  useEffect(() => {
    if (user && user.email) {
      const sid = localStorage.getItem('sustek_session_id');
      if (sid) startHeartbeat(sid);
    }
    return () => stopHeartbeat();
  }, [user, startHeartbeat, stopHeartbeat]);

  const login = async (email, password) => {
    try {
      const { data } = await loginApi(email, password);
      if (data.token) {
        localStorage.setItem('sustek_token', data.token);
      }
      if (data.session_id) {
        localStorage.setItem('sustek_session_id', data.session_id);
      }
      setUser(data);
      return { success: true };
    } catch (e) {
      return { success: false, error: formatApiError(e.response?.data?.detail) };
    }
  };

  const register = async (email, password, name, role) => {
    try {
      const { data } = await registerApi(email, password, name, role);
      if (data.token) {
        localStorage.setItem('sustek_token', data.token);
      }
      setUser(data);
      return { success: true };
    } catch (e) {
      return { success: false, error: formatApiError(e.response?.data?.detail) };
    }
  };

  const logout = async () => {
    stopHeartbeat();
    try { await logoutApi(); } catch { /* ok */ }
    localStorage.removeItem('sustek_token');
    localStorage.removeItem('sustek_session_id');
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
}
